// 動き・大きさ・プラグインの窓口（段階 C の2。tools/chibi-unify/CLASSIC-UI.md）。
// d3 は 2026-10-26 の 7g-1 で自前の部品（selection.ts・transition.ts・ease.ts・interpolate.ts・scale.ts。まとめて sel.ts の S）に入れかえた。
// jQuery は 7g-2 で jq.ts に、プラグインは 7g-3 で自前に（スクロールバー scrollbar.ts・吹き出し tooltip.ts・色を選ぶ画面 colorpicker.ts・円弧の文字は complete.ts の drawArcText）。
// classic の TS は d3・jQuery を使わない（2026-10-26）

import { easeBackOut, easeCircleInOut, easeElasticOut, easeLinear, type Ease } from './ease.js';
import { interpolateRgb as interpolateRgbOf } from './interpolate.js';
import { event, select, selectAll, type Selection } from './selection.js';
import * as jq from './jq.js';
import * as scrollbar from './scrollbar.js';
import { colorPicker as colorPickerOf } from './colorpicker.js';
const w = window as unknown as { charat: { common: Record<string, (...args: any[]) => unknown> } };

// ---- 選択（前は d3 の選択。エンジンの SVG の入れ物 chibi.svgColor なども同じ形） ----

/** 要素を選択にする（前のエンジン・画面の部品が chibi.svgColor などを選択として使うので、置くときはこれで包む） */
export function d3Select(target: string | Element | null): Selection {
  return select(target);
}

// ---- 大きさ・CSS・動き（jq.ts。jQuery と同じ決まり。2026-10-26 の 7g-2） ----

export const height = (sel: string): number => jq.height(sel);
export const setHeight = (sel: string, value: number): void => jq.setHeight(sel, value);
export const width = (sel: string): number => jq.width(sel);
export const setCss = (sel: string, name: string, value: string | number): void => jq.setCss(sel, name, value);
/** 要素の CSS（jQuery の $(el).css(名前, 値) と同じ書き方） */
export const setCssEl = (el: Element, name: string, value: string | number): void => jq.setCss(el, name, value);
/** 要素の文書の上での上の位置（無ければ null） */
export const offsetTop = (sel: string | Element): number | null => {
  const o = jq.offset(sel);
  return o ? o.top : null;
};
/** 要素の文書の上での左の位置（無ければ null） */
export const offsetLeft = (sel: string | Element): number | null => {
  const o = jq.offset(sel);
  return o ? o.left : null;
};
export const exists = (sel: string): boolean => jq.els(sel).length > 0;
/** 入れ物の中の要素の CSS */
export const setCssIn = (sel: string, inner: string, name: string, value: string | number): void => {
  for (const box of jq.els(sel)) for (const el of Array.prototype.slice.call(box.querySelectorAll(inner)) as Element[]) jq.setCss(el, name, value);
};
/** 横のスクロールの位置をすぐ変える */
export const setScrollLeft = (sel: string, left: number): void => {
  for (const el of jq.els(sel)) el.scrollLeft = left;
};

// ---- 動き ----

/** フェードで出す（同じ要素の動きは順番に並ぶ） */
export const fadeIn = (sel: string, duration: number): void => jq.fadeIn(sel, duration);
/** フェードで消す */
export const fadeOut = (sel: string, duration: number, complete?: () => void): void => jq.fadeOut(sel, duration, complete);
/** 動かす（パーツの一覧の開け閉め。速さの変わり方は jQuery UI の名前） */
export const animate = (sel: string, props: Record<string, number | string>, dur: number, easing: string, complete?: () => void): void => jq.animate(sel, props, dur, easing, complete);
/** CSS の値（計算後の値の文字） */
export const getCss = (sel: string, name: string): string => jq.css(sel, name);
/** 縦のスクロールを動かす（200ms） */
export const scrollTopTo = (sel: string, top: number): void => jq.animate(sel, { scrollTop: top }, 'fast');
/** 横のスクロールを動かす（200ms） */
export const scrollLeftTo = (sel: string, left: number): void => jq.animate(sel, { scrollLeft: left }, 'fast');
/**
 * 選んだ色・柄の所のカーソル（円 2 つ）を弾ませる（d3 の transition、200ms・easeElastic）。
 * 先に位置・大きさ・色をすぐ付けて（from）、そこから to へ動かす（同じ要素の前の動きは止まる。d3 と同じ）
 */
export function bounceCursor(circles: Element[], from: (i: number) => { cy?: number; cx?: number; r: number; stroke: string }, to: (i: number) => { r: number; stroke: string }): void {
  const sel = selectAll(circles);
  const f = circles.map((_, i) => from(i));
  if (f[0] && f[0].cy !== undefined) sel.attr('cy', (_d: unknown, i: number) => f[i].cy);
  if (f[0] && f[0].cx !== undefined) sel.attr('cx', (_d: unknown, i: number) => f[i].cx);
  sel
    .attr('r', (_d: unknown, i: number) => f[i].r)
    .attr('stroke', (_d: unknown, i: number) => f[i].stroke)
    .transition()
    .duration(200)
    .ease(easeElasticOut)
    .attr('stroke', to(0).stroke)
    .attr('r', (_d: unknown, i: number) => to(i).r);
}

/**
 * 縦に引っぱる（前は d3.drag。押した所から動いた縦の量 dy を、要素の親（拡大していればその中の座標）で onDrag に渡す。
 * マウスは左のボタンだけ、引っぱったあとのクリックは止める、触って引っぱるときはページのスクロールをしない・文字を選ばない＝d3.drag と同じ）
 */
export function dragY(el: Element, onDrag: (dy: number) => void): void {
  const container = el.parentNode as Element;
  const pointY = (clientX: number, clientY: number): number => {
    const ctm = (container as SVGGraphicsElement).getScreenCTM ? (container as SVGGraphicsElement).getScreenCTM() : null;
    if (ctm) return new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse()).y;
    const rect = container.getBoundingClientRect();
    return clientY - rect.top - container.clientTop;
  };
  const noevent = (ev: Event): void => {
    ev.preventDefault();
    ev.stopImmediatePropagation();
  };
  let lastY = 0, moved = false, startX = 0, startY = 0, touchEnding = 0;
  const touches: Record<number, number> = {};
  const view = select(window as unknown as Element);
  const disable = (): void => {
    view.on('dragstart.drag', function () {
      noevent(event()!);
    }, true);
    view.on('selectstart.drag', function () {
      noevent(event()!);
    }, true);
  };
  const enable = (wasMoved: boolean): void => {
    view.on('dragstart.drag selectstart.drag', null);
    if (wasMoved) {
      view.on('click.drag', function () {
        noevent(event()!);
      }, true);
      setTimeout(() => view.on('click.drag', null), 0);
    }
  };
  const me = select(el);
  me.on('mousedown.drag', function () {
    const ev = event() as MouseEvent;
    if (touchEnding || ev.button) return;
    view.on('mousemove.drag', function () {
      const e = event() as MouseEvent;
      noevent(e);
      if (!moved) {
        const dx = e.clientX - startX, dy = e.clientY - startY;
        moved = dx * dx + dy * dy > 0;
      }
      const y = pointY(e.clientX, e.clientY);
      onDrag(y - lastY);
      lastY = y;
    }, true);
    view.on('mouseup.drag', function () {
      view.on('mousemove.drag mouseup.drag', null);
      enable(moved);
      noevent(event()!);
    }, true);
    disable();
    ev.stopImmediatePropagation();
    moved = false;
    startX = ev.clientX;
    startY = ev.clientY;
    lastY = pointY(ev.clientX, ev.clientY);
  });
  if (!('ontouchstart' in el)) return;
  me.on('touchstart.drag', function () {
    const ev = event() as TouchEvent;
    for (let k = 0; k < ev.changedTouches.length; k++) {
      const t = ev.changedTouches[k];
      ev.stopImmediatePropagation();
      touches[t.identifier] = pointY(t.clientX, t.clientY);
    }
  })
    .on('touchmove.drag', function () {
      const ev = event() as TouchEvent;
      for (let k = 0; k < ev.changedTouches.length; k++) {
        const t = ev.changedTouches[k];
        if (!(t.identifier in touches)) continue;
        noevent(ev);
        const y = pointY(t.clientX, t.clientY);
        onDrag(y - touches[t.identifier]);
        touches[t.identifier] = y;
      }
    })
    .on('touchend.drag touchcancel.drag', function () {
      const ev = event() as TouchEvent;
      if (touchEnding) clearTimeout(touchEnding);
      touchEnding = window.setTimeout(() => {
        touchEnding = 0;
      }, 500);
      for (let k = 0; k < ev.changedTouches.length; k++) {
        const t = ev.changedTouches[k];
        if (!(t.identifier in touches)) continue;
        ev.stopImmediatePropagation();
        delete touches[t.identifier];
      }
    })
    .style('touch-action', 'none')
    .style('-webkit-tap-highlight-color', 'rgba(0,0,0,0)');
}
/**
 * クリックを付ける（ほかの所が S.select(...).on("click")() で取り出して呼ぶもの。カテゴリのボタン。
 * 呼ぶ所を描き直したら dom.ts の onClick にする）。押されたときは要素のデータを 1 つ目の引数で渡し、取り出して呼ぶ所は引数なしで呼ぶ（d3 と同じ）
 */
export function d3Click(el: Element, listener: (d?: unknown) => void): void {
  select(el).on('click', listener);
}
/** ほかの所が付けたクリックを取り出して呼ぶ（メニューのボタン。S.select(sel).on("click")(arg)。呼ぶ所を描き直したらボタンの定義の関数を直接呼ぶ） */
export function d3ClickCall(sel: string, arg?: unknown): void {
  (select(sel).on('click') as unknown as (a?: unknown) => void)(arg);
}
/** 押す（付けた処理と、ブラウザのクリック。色を自由に選ぶ画面を開く隠しの input） */
export const jqClick = (sel: string): void => jq.click(sel);
/** 動きの速さの変わり方（moveButton に渡す。elastic は弾む・circle はゆっくり始まりゆっくり止まる） */
export const ease = (name: 'elastic' | 'linear' | 'backOut' | 'circle'): Ease =>
  name === 'elastic' ? easeElasticOut : name === 'linear' ? easeLinear : name === 'backOut' ? easeBackOut : easeCircleInOut;
/** 2 色の間の色（t は 0〜1、返すのは "rgb(r, g, b)"） */
export const interpolateRgb = (a: string, b: string): ((t: number) => string) => interpolateRgbOf(a, b);

// ---- プラグイン ----

/** 縦のスクロールバー（PC。色の列・柄の一覧と同じ設定。太さ 8px。scrollbar.ts） */
export function scrollbarY(sel: string): void {
  scrollbar.init(sel, { axis: 'y', scrollInertia: 400, mouseWheelPixels: 250, autoHideScrollbar: true });
  jq.setCss(sel + ' .mCSB_scrollTools', 'width', 8);
  jq.setCss(sel + ' .mCSB_dragger_bar', 'width', 8);
  jq.setCss(sel + ' .mCSB_container', 'margin-right', 0);
}
/** 横のスクロールバー（PC。カテゴリ・パーツの欄。太さ 8px） */
export function scrollbarX(sel: string): void {
  scrollbar.init(sel, { axis: 'x', scrollInertia: 400, mouseWheelPixels: 150, autoHideScrollbar: false });
  jq.setCss(sel + ' .mCSB_scrollTools', 'height', 8);
  jq.setCss(sel + ' .mCSB_dragger_bar', 'height', 8);
}
/** スクロールバーを止める（aReset: 止めるとき位置を先頭に戻すか） */
export function scrollbarDisable(sel: string, aReset: boolean): void {
  scrollbar.disable(sel, aReset);
}
/** スクロールバーを動かして、中の大きさに合わせ直す */
export function scrollbarEnable(sel: string): void {
  scrollbar.refresh(sel);
}
/** スクロールバーで動かす（to は [縦, 横]、時間は既定 200ms） */
export function scrollbarTo(sel: string, to: [number, number], inertia = 200): void {
  scrollbar.scrollToPos(sel, to, inertia);
}
/** 色を自由に選ぶ画面（前は jQuery の excolor。colorpicker.ts）。OK で onOk */
export function colorPicker(inputSel: string, rootPath: string, onOk: () => void): void {
  colorPickerOf(inputSel, rootPath, onOk);
}
export const inputValue = (sel: string): string => jq.val(sel);
export const setInputValue = (sel: string, value: string): void => {
  jq.setVal(sel, value);
};

// ---- まだ描き直していない前のエンジンの部品（描き直したらここから外す） ----

/** 浮いた丸いボタン（charat.common.makeFloatButton。右のメニュー・ボタンの部品＝CLASSIC-UI.md の 5 で描き直す） */
export const floatButton = (def: Record<string, unknown>): void => {
  w.charat.common.makeFloatButton(def);
};
/** 操作のヒントの吹き出し（charat.common.viewTooltip。tippy） */
export const tooltip = (...args: unknown[]): void => {
  w.charat.common.viewTooltip(...args);
};
